import * as React from 'react';
import InputMask from 'react-input-mask';
import NumberFormat from 'react-number-format';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Row } from 'reactstrap';
import { IUser } from 'src/model';
import { IUpdateUserDto } from 'src/model/dto/user-dto';
import { updateUser } from 'src/services';
import { isMobilePhoneNumber , isNumber, isPhoneNumber } from 'src/utils/validation';
import { ProfilePictureInputWithGlobalContext } from './ProfilePicture/ProfilePictureInput';

interface IContactEditionProps {
  user: IUser;
  refreshUser: (logout?: boolean) => void;
}

export const ContactEdition: React.SFC<IContactEditionProps> = ({
  user,
  refreshUser,
}) => {
  const [state, setState] = React.useState({
    address: { ...user.address },
    company: { ...user.company },
    edit: false,
    email: user.email,
    emailChange: false,
    firstname: user.firstname,
    invalid: false,
    lastname: user.lastname,
  });

  const companyCodeValue =  state.company.companyCode && state.company.companyCode.replace(/\s|_/g, '');
  const isValid =
    (!!state.lastname ) && 
    (!!!state.address.zipcode || isNumber(state.address.zipcode)) &&
    (!!!state.address.phone || isPhoneNumber(state.address.phone)) &&
    (!!state.address.mobilephone && isMobilePhoneNumber(state.address.mobilephone)) &&
    (!!!state.company.companyCode ||  companyCodeValue .length=== 14 )  &&
    (!!!state.company.zipcode || isNumber(state.company.zipcode));
  
  const toggleEdit = () => {
    setState({
      address: { ...user.address },
      company: { ...user.company },
      edit: !state.edit,
      email: user.email,
      emailChange: false,
      firstname: user.firstname,
      invalid: false,
      lastname: user.lastname,
    });
  };

  const handleChange = (e: React.ChangeEvent<Element>) => {
    if ((e.target as any).name === 'email') {
      setState({ ...state, email: (e.target as any).value, emailChange: true });
    } else {
      setState({
        ...state,
        [(e.target as any).name]: (e.target as any).value,
      });
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<Element>) => {
    setState({
      ...state,
      address: {
        ...state.address,
        [(e.target as any).name]: (e.target as any).value,
      },
    });
  };

  const handleCompanyChange = (e: React.ChangeEvent<Element>) => {
    setState({
      ...state,
      company: {
        ...state.company,
        [(e.target as any).name]: (e.target as any).value,
      },
    });
  };

  const handleSave = async () => {
    const { address, company, email, firstname, lastname } = state;
    const updateUserDto: IUpdateUserDto = {
      address,
      company,
      email,
      firstname,
      lastname,
    };

    if (isValid) {
      try {
        const updatedUser = await updateUser(updateUserDto);
        if (updatedUser) {
          refreshUser();
          setState({
            address: { ...updatedUser.address },
            company: { ...updatedUser.company },
            edit: false,
            email: updatedUser.email,
            emailChange: false,
            firstname: updatedUser.firstname,
            invalid: false,
            lastname: updatedUser.lastname,
          });
        }
      } catch (error) {
        setState({
          ...state,
          invalid: true,
        });
      }
    } else {
      setState({
        ...state,
        invalid: true,
      });
    }
  };

  return (
    <Form className='p-5'>
      <Row>
        <Col md='12'>
          <span className='text-dark' style={{ fontSize: '32px' }}>
            Coordonnées
          </span>
        </Col>
      </Row>
      <Row className='d-flex justify-content-between mt-3'>
        <Col md='5' xs='auto'>
          <ProfilePictureInputWithGlobalContext />
          <Row className='mt-3'>
            <Col md='12' xs='auto'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>Nom</span>
                <Input
                  type='text'
                  name='lastname'
                  placeholder='Nom*'
                  disabled={!state.edit}
                  invalid={state.invalid && !!!state.lastname}
                  value={state.lastname ? state.lastname : ''}
                  onChange={handleChange}
                />
                 <FormFeedback>Merci de renseigner ce champ</FormFeedback>
              </FormGroup>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>Prénom</span>
                <Input
                  type='text'
                  name='firstname'
                  placeholder='Prénom'
                  disabled={!state.edit}
                  value={state.firstname ? state.firstname : ''}
                  onChange={handleChange}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <span className='d-block font-weight-bolder mb-2'>Adresse</span>
            <Input
              type='text'
              name='address1'
              placeholder='Adresse'
              disabled={!state.edit}
              value={state.address.address1 ? state.address.address1 : ''}
              onChange={handleAddressChange}
            />
          </FormGroup>
          <Row className='d-flex justify-content-between'>
            <Col md='6' xs='auto'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>
                  Code postal
                </span>
                <Input
                  type='text'
                  name='zipcode'
                  placeholder='Code postal'
                  disabled={!state.edit}
                  invalid={
                     state.invalid &&
                     !!state.address.zipcode &&
                     !isNumber(state.address.zipcode)
                   }
                  value={state.address.zipcode ? state.address.zipcode : ''}
                  onChange={handleAddressChange}
                  tag={NumberFormat}
                  format="#####" 
                />
              </FormGroup>
            </Col>
            <Col md='6' xs='auto'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>Ville</span>
                <Input
                  type='text'
                  name='city'
                  placeholder='Ville'
                  disabled={!state.edit}
                  value={state.address.city ? state.address.city : ''}
                  onChange={handleAddressChange}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md='12'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>
                  Adresse e-mail
                </span>
                <Input
                  type='text'
                  name='email'
                  placeholder='E-mail'
                  disabled={!state.edit}
                  invalid={state.invalid && state.emailChange }
                  value={state.email ? state.email : ''}
                  onChange={handleChange}
                />
                {state.emailChange && (
                  <div
                    className='text-warning mt-2'
                    style={{ fontSize: '10px' }}
                  >
                    Cette nouvelle adresse e-mail sera votre nouvel identifiant
                    de connexion
                  </div>
                )}
                <FormFeedback>
                  Cet e-mail est déjà associé à un compte
                </FormFeedback>
              </FormGroup>
            </Col>
          </Row>
          <Row className='d-flex justify-content-between'>
          <Col md='6' xs='auto'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>Mobile</span>
                <Input
                  type='text'
                  name='mobilephone'
                  placeholder='Téléphone mobile*'
                  disabled={!state.edit}
                  invalid={
                    state.invalid &&
                    (!!!state.address.mobilephone ||
                      !isMobilePhoneNumber(state.address.mobilephone))
                  }
                  value={
                    state.address.mobilephone ? state.address.mobilephone : ''
                  }
                  onChange={handleAddressChange}
                  tag={InputMask}
                  mask="+3\3 9 99 99 99 99" 
                  maskChar={null} 
                />
              </FormGroup>
            </Col>
            <Col md='6' xs='auto'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>Fixe</span>
                <Input
                  type='text'
                  name='phone'
                  placeholder='Téléphone fixe'
                  disabled={!state.edit}
                  invalid={
                    state.invalid &&
                    !!state.address.phone &&
                    !isPhoneNumber(state.address.phone)
                  }
                  value={state.address.phone ? state.address.phone : ''}
                  onChange={handleAddressChange}
                  tag={InputMask}
                  mask="+3\3 9 99 99 99 99" 
                  maskChar={null} 
                />
              </FormGroup>
            </Col>
          </Row>
        </Col>
        <Col md='5' xs='auto'>
          <FormGroup>
            <span className='d-block font-weight-bolder mb-2'>Société</span>
            <Input
              type='text'
              name='companyName'
              placeholder='Nom de votre société'
              disabled={!state.edit}
              value={state.company.companyName ? state.company.companyName : ''}
              onChange={handleCompanyChange}
            />
          </FormGroup>
          <FormGroup>
            <span className='d-block font-weight-bolder mb-2'>SIRET</span>
            <Input
              type='text'
              name='companyCode'
              placeholder='SIRET de votre société'
              disabled={!state.edit}
              invalid={
                state.invalid  || (!!state.company.companyCode &&  companyCodeValue.length !== 14 )
               }
              value={state.company.companyCode ? state.company.companyCode : ''}
              onChange={handleCompanyChange}
              tag={NumberFormat}
              format="### ### ### #####" 
              mask='_'
            />
          </FormGroup>
          <FormGroup>
            <span className='d-block font-weight-bolder mb-2'>
              Adresse société
            </span>
            <Input
              type='text'
              name='address1'
              placeholder='Adresse de votre société'
              disabled={!state.edit}
              value={state.company.address1 ? state.company.address1 : ''}
              onChange={handleCompanyChange}
            />
          </FormGroup>
          <Row className='d-flex justify-content-between'>
            <Col md='6' xs='auto'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>
                  Code postal
                </span>
                <Input
                  type='text'
                  name='zipcode'
                  placeholder='Code postal'
                  disabled={!state.edit}
                  invalid={
                    state.invalid &&
                    (!!state.company.zipcode &&
                      !isNumber(state.company.zipcode))
                  }
                  value={state.company.zipcode ? state.company.zipcode : ''}
                  onChange={handleCompanyChange}
                  tag={NumberFormat}
                  format="#####" 
                />
              </FormGroup>
            </Col>
            <Col md='6' xs='auto'>
              <FormGroup>
                <span className='d-block font-weight-bolder mb-2'>Ville</span>
                <Input
                  type='text'
                  name='city'
                  placeholder='Ville'
                  disabled={!state.edit}
                  value={state.company.city ? state.company.city : ''}
                  onChange={handleCompanyChange}
                />
              </FormGroup>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className='d-flex justify-content-between mt-4'>
        {!state.edit ? (
          <Col md='3'>
            <Button
              className='text-uppercase text-white bg-success border-0'
              onClick={toggleEdit}
            >
              Modifier
            </Button>
          </Col>
        ) : (
          <>
            <Col md='3'>
              <Button
                className='text-uppercase text-white bg-dark border-0'
                onClick={toggleEdit}
              >
                Annuler
              </Button>
            </Col>
            <Col md='3'>
              <Button
                className='text-uppercase text-white bg-success border-0 float-right'
                onClick={handleSave}
              >
                Sauvegarder
              </Button>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};



// WEBPACK FOOTER //
// ./src/components/Profile/ContactEdition.tsx